import { useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { orgBillingApi } from "@/api/org-billing-api";
import { getErrorMessage } from "@/lib/api-error";

export type CardOnFile = {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  holderName?: string;
};

const formatExpiry = (m: number, y: number) =>
  `${String(m).padStart(2, "0")}/${String(y).slice(-2)}`;

export default function PaymentMethodSection({
  card,
  orgId,
}: {
  card?: CardOnFile | null;
  orgId?: string;
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const openSetupIntent = async () => {
    if (!orgId) {
      toast.error("No organization context");
      return;
    }
    setIsUpdating(true);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const session = await orgBillingApi.startPaymentMethodSetup(
        orgId,
        `${returnUrl}?setup_session={CHECKOUT_SESSION_ID}`,
        returnUrl
      );
      window.location.href = session.checkout_url;
    } catch (err) {
      console.error("Card update failed", err);
      toast.error(getErrorMessage(err, "Couldn't start card update"));
      setIsUpdating(false);
    }
  };

  return (
    <section>
      <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        Payment method
      </h2>

      {card ? (
        <div className="mt-3 flex items-center justify-between rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-12 items-center justify-center rounded border border-border bg-muted/40">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {card.brand} •••• {card.last4}
              </p>
              <p className="text-xs text-muted-foreground">
                Expires {formatExpiry(card.expMonth, card.expYear)}
                {card.holderName ? ` · ${card.holderName}` : ""}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openSetupIntent}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Opening…
              </>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex items-center justify-between rounded-lg border border-dashed border-border bg-card p-5">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <CreditCard className="h-4 w-4" />
            No payment method on file
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openSetupIntent}
            disabled={isUpdating}
          >
            {isUpdating ? "Opening…" : "+ Add payment method"}
          </Button>
        </div>
      )}
    </section>
  );
}
